import { useState, useEffect, useCallback } from 'react'
import {
  Box,
  Typography,
  IconButton,
  Chip,
  LinearProgress,
  Fade,
  Zoom,
  Slide,
} from '@mui/material'
import {
  KeyboardArrowLeft,
  KeyboardArrowRight,
  KeyboardArrowDown,
  Download,
  CheckCircle,
  Warning,
  TrendingUp,
  Speed,
  Build,
  Inventory2,
  CalendarMonth,
  Assessment,
  Notifications,
  QrCode2,
  LocalHospital,
  Science,
  AttachMoney,
  Group,
  PhoneAndroid,
  Computer,
  Star,
  Lightbulb,
  Shield,
  Verified,
  PlayArrow,
  Circle,
} from '@mui/icons-material'

// Brand colors
const COLORS = {
  primary: '#1B5E7A',
  secondary: '#2E7D32',
  accent: '#FF6B35',
  dark: '#0a1628',
  light: '#f8f9fa',
  success: '#4CAF50',
  warning: '#FF9800',
  danger: '#F44336',
  info: '#2196F3',
  purple: '#9C27B0',
  teal: '#009688',
  gradient1: 'linear-gradient(135deg, #1B5E7A 0%, #0d3d4d 100%)',
  gradient2: 'linear-gradient(135deg, #2E7D32 0%, #1B5E20 100%)',
  gradient3: 'linear-gradient(135deg, #FF6B35 0%, #E64A19 100%)',
  gradient4: 'linear-gradient(135deg, #9C27B0 0%, #7B1FA2 100%)',
  gradient5: 'linear-gradient(135deg, #0a1628 0%, #1a2744 100%)',
}

// Slide Component
const SlideContainer = ({ children, bg = COLORS.gradient5, active }) => (
  <Fade in={active} timeout={500}>
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: bg,
        display: active ? 'flex' : 'none',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: { xs: 4, md: 8 },
        overflow: 'hidden',
      }}
    >
      {children}
    </Box>
  </Fade>
)

// Animated Icon Box
const IconBox = ({ children, color, size = 80, delay = 0 }) => (
  <Zoom in={true} style={{ transitionDelay: `${delay}ms` }}>
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: 4,
        background: `${color}20`,
        border: `2px solid ${color}40`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: color,
        mb: 3,
      }}
    >
      {children}
    </Box>
  </Zoom>
)

// Feature Item for grid slides
const FeatureItem = ({ icon, title, description, color, delay }) => (
  <Zoom in={true} style={{ transitionDelay: `${delay}ms` }}>
    <Box
      sx={{
        textAlign: 'center',
        p: 3,
        borderRadius: 4,
        background: 'rgba(255,255,255,0.05)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255,255,255,0.1)',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-8px)',
          background: 'rgba(255,255,255,0.1)',
          borderColor: color,
        },
      }}
    >
      <Box
        sx={{
          width: 64,
          height: 64,
          borderRadius: 3,
          background: `${color}20`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mx: 'auto',
          mb: 2,
          color: color,
        }}
      >
        {icon}
      </Box>
      <Typography variant="h6" fontWeight={700} color="white" gutterBottom>
        {title}
      </Typography>
      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
        {description}
      </Typography>
    </Box>
  </Zoom>
)

// Stat Item
const StatItem = ({ number, label, color, delay }) => (
  <Zoom in={true} style={{ transitionDelay: `${delay}ms` }}>
    <Box sx={{ textAlign: 'center' }}>
      <Typography
        variant="h1"
        fontWeight={900}
        sx={{
          fontSize: { xs: '3rem', md: '5rem' },
          background: `linear-gradient(135deg, ${color} 0%, white 100%)`,
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          mb: 1,
        }}
      >
        {number}
      </Typography>
      <Typography
        variant="h6"
        sx={{ color: 'rgba(255,255,255,0.8)', fontWeight: 500 }}
      >
        {label}
      </Typography>
    </Box>
  </Zoom>
)

// SWOT Item
const SwotItem = ({ title, items, color, icon, delay }) => (
  <Zoom in={true} style={{ transitionDelay: `${delay}ms` }}>
    <Box
      sx={{
        p: 3,
        borderRadius: 4,
        background: `${color}15`,
        border: `2px solid ${color}40`,
        height: '100%',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            background: color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
          }}
        >
          {icon}
        </Box>
        <Typography variant="h6" fontWeight={700} sx={{ color }}>
          {title}
        </Typography>
      </Box>
      {items.map((item, idx) => (
        <Box
          key={idx}
          sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}
        >
          <CheckCircle sx={{ fontSize: 16, color, mt: 0.3 }} />
          <Typography variant="body2" color="white">
            {item}
          </Typography>
        </Box>
      ))}
    </Box>
  </Zoom>
)

const PresentationPage = () => {
  const [currentSlide, setCurrentSlide] = useState(0)
  const totalSlides = 12

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev < totalSlides - 1 ? prev + 1 : prev))
  }, [totalSlides])

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev > 0 ? prev - 1 : prev))
  }, [])

  const goToSlide = (index) => {
    setCurrentSlide(index)
  }

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        nextSlide()
      } else if (e.key === 'ArrowLeft' || e.key === 'Backspace') {
        e.preventDefault()
        prevSlide()
      } else if (e.key === 'Home') {
        setCurrentSlide(0)
      } else if (e.key === 'End') {
        setCurrentSlide(totalSlides - 1)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [nextSlide, prevSlide, totalSlides])

  const handleDownload = () => {
    const htmlContent = generateDownloadHTML()
    const blob = new Blob([htmlContent], { type: 'text/html' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'DuoNep-Presentation.html'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflow: 'hidden',
        backgroundColor: COLORS.dark,
      }}
    >
      {/* Progress Bar */}
      <LinearProgress
        variant="determinate"
        value={((currentSlide + 1) / totalSlides) * 100}
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 100,
          height: 4,
          backgroundColor: 'rgba(255,255,255,0.1)',
          '& .MuiLinearProgress-bar': {
            background: COLORS.gradient3,
          },
        }}
      />

      {/* Slide Counter */}
      <Typography
        sx={{
          position: 'absolute',
          top: 20,
          left: 24,
          zIndex: 100,
          color: 'rgba(255,255,255,0.5)',
          fontFamily: 'monospace',
          fontSize: '0.9rem',
        }}
      >
        {String(currentSlide + 1).padStart(2, '0')} / {totalSlides}
      </Typography>

      {/* Download Button */}
      <IconButton
        onClick={handleDownload}
        sx={{
          position: 'absolute',
          top: 16,
          right: 24,
          zIndex: 100,
          color: 'white',
          backgroundColor: 'rgba(255,255,255,0.1)',
          '&:hover': { backgroundColor: COLORS.accent },
        }}
      >
        <Download />
      </IconButton>

      {/* Navigation Buttons */}
      <IconButton
        onClick={prevSlide}
        disabled={currentSlide === 0}
        sx={{
          position: 'absolute',
          left: 24,
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 100,
          color: 'white',
          backgroundColor: 'rgba(255,255,255,0.1)',
          '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' },
          '&:disabled': { opacity: 0.3 },
          width: 56,
          height: 56,
        }}
      >
        <KeyboardArrowLeft sx={{ fontSize: 32 }} />
      </IconButton>

      <IconButton
        onClick={nextSlide}
        disabled={currentSlide === totalSlides - 1}
        sx={{
          position: 'absolute',
          right: 24,
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 100,
          color: 'white',
          backgroundColor: 'rgba(255,255,255,0.1)',
          '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' },
          '&:disabled': { opacity: 0.3 },
          width: 56,
          height: 56,
        }}
      >
        <KeyboardArrowRight sx={{ fontSize: 32 }} />
      </IconButton>

      {/* Slide Dots */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 24,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 100,
          display: 'flex',
          gap: 1,
        }}
      >
        {Array.from({ length: totalSlides }).map((_, index) => (
          <IconButton
            key={index}
            onClick={() => goToSlide(index)}
            sx={{
              p: 0.5,
              color:
                currentSlide === index
                  ? COLORS.accent
                  : 'rgba(255,255,255,0.3)',
              transition: 'all 0.3s',
              '&:hover': { color: 'rgba(255,255,255,0.7)' },
            }}
          >
            <Circle sx={{ fontSize: currentSlide === index ? 12 : 8 }} />
          </IconButton>
        ))}
      </Box>

      {/* Keyboard hint */}
      <Typography
        sx={{
          position: 'absolute',
          bottom: 24,
          right: 24,
          zIndex: 100,
          color: 'rgba(255,255,255,0.3)',
          fontSize: '0.75rem',
        }}
      >
        Press Enter or → to continue
      </Typography>

      {/* SLIDES */}

      {/* Slide 1: Title */}
      <SlideContainer active={currentSlide === 0} bg={COLORS.gradient1}>
        <Zoom in={currentSlide === 0}>
          <Box sx={{ textAlign: 'center', maxWidth: 900 }}>
            <Chip
              label="Uganda Cancer Institute"
              sx={{
                backgroundColor: 'rgba(255,255,255,0.15)',
                color: 'white',
                fontWeight: 600,
                mb: 4,
                fontSize: '1rem',
                py: 2.5,
                px: 1,
              }}
            />
            <Typography
              variant="h1"
              fontWeight={900}
              sx={{
                fontSize: { xs: '4rem', md: '8rem' },
                color: 'white',
                letterSpacing: '-0.03em',
                lineHeight: 1,
                mb: 2,
              }}
            >
              DuoNep
            </Typography>
            <Typography
              variant="h4"
              sx={{
                color: 'rgba(255,255,255,0.9)',
                fontWeight: 300,
                mb: 4,
              }}
            >
              Computerized Maintenance Management System
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: 'rgba(255,255,255,0.7)',
                maxWidth: 600,
                mx: 'auto',
                fontWeight: 400,
                lineHeight: 1.8,
              }}
            >
              A comprehensive digital solution for managing medical equipment
              maintenance, inventory, and facility operations
            </Typography>
            <Box sx={{ mt: 6, display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
              {['Asset Management', 'Work Orders', 'Preventive Maintenance', 'Inventory', 'Calibration'].map((item) => (
                <Chip
                  key={item}
                  label={item}
                  size="small"
                  sx={{
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    color: 'white',
                  }}
                />
              ))}
            </Box>
          </Box>
        </Zoom>
      </SlideContainer>

      {/* Slide 2: The Problem */}
      <SlideContainer active={currentSlide === 1} bg={COLORS.gradient5}>
        <Box sx={{ textAlign: 'center', maxWidth: 1000 }}>
          <IconBox color={COLORS.danger} size={100}>
            <Warning sx={{ fontSize: 50 }} />
          </IconBox>
          <Typography
            variant="h2"
            fontWeight={800}
            color="white"
            sx={{ mb: 2 }}
          >
            The Challenge
          </Typography>
          <Typography
            variant="h5"
            sx={{ color: 'rgba(255,255,255,0.7)', mb: 6, fontWeight: 400 }}
          >
            Healthcare facilities face critical challenges in equipment management
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
              gap: 3,
            }}
          >
            {[
              { title: 'Equipment Downtime', desc: 'Unexpected failures cause treatment delays', color: COLORS.danger },
              { title: 'Manual Tracking', desc: 'Paper records get lost, schedules missed', color: COLORS.warning },
              { title: 'Inventory Chaos', desc: 'No visibility into spare parts stock', color: COLORS.purple },
              { title: 'Compliance Risk', desc: 'Difficulty maintaining audit trails', color: COLORS.info },
            ].map((item, idx) => (
              <Zoom key={idx} in={currentSlide === 1} style={{ transitionDelay: `${idx * 100}ms` }}>
                <Box
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    borderLeft: `4px solid ${item.color}`,
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    textAlign: 'left',
                  }}
                >
                  <Typography variant="h6" color="white" fontWeight={700}>
                    {item.title}
                  </Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.6)' }}>
                    {item.desc}
                  </Typography>
                </Box>
              </Zoom>
            ))}
          </Box>
        </Box>
      </SlideContainer>

      {/* Slide 3: The Solution */}
      <SlideContainer active={currentSlide === 2} bg={COLORS.gradient2}>
        <Box sx={{ textAlign: 'center', maxWidth: 900 }}>
          <IconBox color="white" size={100}>
            <Lightbulb sx={{ fontSize: 50 }} />
          </IconBox>
          <Typography
            variant="h2"
            fontWeight={800}
            color="white"
            sx={{ mb: 2 }}
          >
            Our Solution
          </Typography>
          <Typography
            variant="h5"
            sx={{ color: 'rgba(255,255,255,0.9)', mb: 3, fontWeight: 400 }}
          >
            DuoNep - One Platform for All Maintenance Operations
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: 'rgba(255,255,255,0.8)',
              maxWidth: 700,
              mx: 'auto',
              fontSize: '1.1rem',
              lineHeight: 1.8,
            }}
          >
            A modern, web and mobile-based system that digitizes and streamlines
            all maintenance workflows - from fault reporting to preventive maintenance,
            from inventory management to compliance tracking.
          </Typography>
          <Box sx={{ mt: 5, display: 'flex', justifyContent: 'center', gap: 3 }}>
            <Zoom in={currentSlide === 2} style={{ transitionDelay: '200ms' }}>
              <Box sx={{ textAlign: 'center' }}>
                <Computer sx={{ fontSize: 48, color: 'white', mb: 1 }} />
                <Typography color="white" fontWeight={600}>Web App</Typography>
              </Box>
            </Zoom>
            <Zoom in={currentSlide === 2} style={{ transitionDelay: '400ms' }}>
              <Box sx={{ textAlign: 'center' }}>
                <PhoneAndroid sx={{ fontSize: 48, color: 'white', mb: 1 }} />
                <Typography color="white" fontWeight={600}>Mobile App</Typography>
              </Box>
            </Zoom>
          </Box>
        </Box>
      </SlideContainer>

      {/* Slide 4: Key Features Grid */}
      <SlideContainer active={currentSlide === 3} bg={COLORS.gradient5}>
        <Box sx={{ textAlign: 'center', width: '100%', maxWidth: 1200 }}>
          <Typography variant="h2" fontWeight={800} color="white" sx={{ mb: 5 }}>
            Key Features
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
              gap: 3,
            }}
          >
            <FeatureItem
              icon={<LocalHospital sx={{ fontSize: 32 }} />}
              title="Asset Management"
              description="Complete lifecycle tracking with QR codes"
              color={COLORS.primary}
              delay={0}
            />
            <FeatureItem
              icon={<Build sx={{ fontSize: 32 }} />}
              title="Work Orders"
              description="Streamlined maintenance workflows"
              color={COLORS.secondary}
              delay={100}
            />
            <FeatureItem
              icon={<CalendarMonth sx={{ fontSize: 32 }} />}
              title="Preventive Maintenance"
              description="Automated scheduling & tracking"
              color={COLORS.accent}
              delay={200}
            />
            <FeatureItem
              icon={<Inventory2 sx={{ fontSize: 32 }} />}
              title="Inventory Control"
              description="Real-time stock management"
              color={COLORS.purple}
              delay={300}
            />
            <FeatureItem
              icon={<Speed sx={{ fontSize: 32 }} />}
              title="Calibration"
              description="Schedule & certificate tracking"
              color={COLORS.teal}
              delay={400}
            />
            <FeatureItem
              icon={<Science sx={{ fontSize: 32 }} />}
              title="Oncology"
              description="Specialized QA/QC & source tracking"
              color={COLORS.danger}
              delay={500}
            />
          </Box>
        </Box>
      </SlideContainer>

      {/* Slide 5: Asset Management */}
      <SlideContainer active={currentSlide === 4} bg={COLORS.gradient1}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 8, maxWidth: 1100 }}>
          <Zoom in={currentSlide === 4}>
            <Box
              sx={{
                width: 200,
                height: 200,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <LocalHospital sx={{ fontSize: 100, color: 'white' }} />
            </Box>
          </Zoom>
          <Box>
            <Typography variant="h2" fontWeight={800} color="white" sx={{ mb: 3 }}>
              Asset Management
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {[
                'Complete asset registry with specifications',
                'QR code generation & mobile scanning',
                'Maintenance history tracking',
                'Depreciation & lifecycle management',
                'Department-wise allocation',
                'Asset transfer & disposal workflows',
              ].map((item, idx) => (
                <Zoom key={idx} in={currentSlide === 4} style={{ transitionDelay: `${idx * 100}ms` }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <CheckCircle sx={{ color: COLORS.success }} />
                    <Typography variant="h6" color="white" fontWeight={400}>
                      {item}
                    </Typography>
                  </Box>
                </Zoom>
              ))}
            </Box>
          </Box>
        </Box>
      </SlideContainer>

      {/* Slide 6: Work Orders */}
      <SlideContainer active={currentSlide === 5} bg={COLORS.gradient2}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 8, maxWidth: 1100, flexDirection: { xs: 'column', md: 'row-reverse' } }}>
          <Zoom in={currentSlide === 5}>
            <Box
              sx={{
                width: 200,
                height: 200,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Build sx={{ fontSize: 100, color: 'white' }} />
            </Box>
          </Zoom>
          <Box>
            <Typography variant="h2" fontWeight={800} color="white" sx={{ mb: 3 }}>
              Work Order System
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {[
                'Easy fault reporting from web or mobile',
                'Automatic technician assignment',
                'Priority-based task management',
                'Labor & parts cost tracking',
                'Job card generation (PDF)',
                'Real-time status notifications',
              ].map((item, idx) => (
                <Zoom key={idx} in={currentSlide === 5} style={{ transitionDelay: `${idx * 100}ms` }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <CheckCircle sx={{ color: 'white' }} />
                    <Typography variant="h6" color="white" fontWeight={400}>
                      {item}
                    </Typography>
                  </Box>
                </Zoom>
              ))}
            </Box>
          </Box>
        </Box>
      </SlideContainer>

      {/* Slide 7: Preventive Maintenance */}
      <SlideContainer active={currentSlide === 6} bg={COLORS.gradient3}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 8, maxWidth: 1100 }}>
          <Zoom in={currentSlide === 6}>
            <Box
              sx={{
                width: 200,
                height: 200,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <CalendarMonth sx={{ fontSize: 100, color: 'white' }} />
            </Box>
          </Zoom>
          <Box>
            <Typography variant="h2" fontWeight={800} color="white" sx={{ mb: 3 }}>
              Preventive Maintenance
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {[
                'PM templates by equipment type',
                'Automated schedule generation',
                'Visual Gantt chart planning',
                'Resource & cost estimation',
                'Technician calendar view',
                'Compliance reporting',
              ].map((item, idx) => (
                <Zoom key={idx} in={currentSlide === 6} style={{ transitionDelay: `${idx * 100}ms` }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <CheckCircle sx={{ color: 'white' }} />
                    <Typography variant="h6" color="white" fontWeight={400}>
                      {item}
                    </Typography>
                  </Box>
                </Zoom>
              ))}
            </Box>
          </Box>
        </Box>
      </SlideContainer>

      {/* Slide 8: More Features */}
      <SlideContainer active={currentSlide === 7} bg={COLORS.gradient4}>
        <Box sx={{ textAlign: 'center', maxWidth: 1000 }}>
          <Typography variant="h2" fontWeight={800} color="white" sx={{ mb: 5 }}>
            And Much More...
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
              gap: 3,
            }}
          >
            {[
              { icon: <Inventory2 />, label: 'Inventory' },
              { icon: <Speed />, label: 'Calibration' },
              { icon: <QrCode2 />, label: 'QR Codes' },
              { icon: <Notifications />, label: 'Notifications' },
              { icon: <Assessment />, label: 'Reports' },
              { icon: <Group />, label: 'User Roles' },
              { icon: <Science />, label: 'Rad Oncology' },
              { icon: <AttachMoney />, label: 'Cost Tracking' },
            ].map((item, idx) => (
              <Zoom key={idx} in={currentSlide === 7} style={{ transitionDelay: `${idx * 50}ms` }}>
                <Box
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    background: 'rgba(255,255,255,0.1)',
                    textAlign: 'center',
                  }}
                >
                  <Box sx={{ color: 'white', mb: 1 }}>{item.icon}</Box>
                  <Typography color="white" fontWeight={600}>
                    {item.label}
                  </Typography>
                </Box>
              </Zoom>
            ))}
          </Box>
        </Box>
      </SlideContainer>

      {/* Slide 9: Expected Impact */}
      <SlideContainer active={currentSlide === 8} bg={COLORS.gradient5}>
        <Box sx={{ textAlign: 'center', maxWidth: 1000 }}>
          <Typography variant="h2" fontWeight={800} color="white" sx={{ mb: 2 }}>
            Expected Impact
          </Typography>
          <Typography variant="h5" sx={{ color: 'rgba(255,255,255,0.7)', mb: 6 }}>
            Measurable improvements in maintenance operations
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
              gap: 4,
            }}
          >
            <StatItem number="40%" label="Reduced Downtime" color={COLORS.success} delay={0} />
            <StatItem number="60%" label="Faster Response" color={COLORS.info} delay={150} />
            <StatItem number="100%" label="Audit Compliance" color={COLORS.warning} delay={300} />
            <StatItem number="25%" label="Cost Savings" color={COLORS.purple} delay={450} />
          </Box>
        </Box>
      </SlideContainer>

      {/* Slide 10: SWOT Analysis */}
      <SlideContainer active={currentSlide === 9} bg={COLORS.gradient5}>
        <Box sx={{ textAlign: 'center', width: '100%', maxWidth: 1100 }}>
          <Typography variant="h2" fontWeight={800} color="white" sx={{ mb: 4 }}>
            SWOT Analysis
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
              gap: 2,
            }}
          >
            <SwotItem
              title="Strengths"
              color={COLORS.success}
              icon={<Star />}
              delay={0}
              items={[
                'Custom-built for UCI needs',
                'Web + Mobile accessibility',
                'Role-based access control',
                'Real-time notifications',
              ]}
            />
            <SwotItem
              title="Weaknesses"
              color={COLORS.warning}
              icon={<Warning />}
              delay={100}
              items={[
                'Initial data migration effort',
                'Staff training required',
                'Internet dependency',
              ]}
            />
            <SwotItem
              title="Opportunities"
              color={COLORS.info}
              icon={<Lightbulb />}
              delay={200}
              items={[
                'Integration with HIS systems',
                'Expansion to other facilities',
                'Predictive maintenance (AI)',
                'IoT sensor integration',
              ]}
            />
            <SwotItem
              title="Threats"
              color={COLORS.danger}
              icon={<Shield />}
              delay={300}
              items={[
                'Resistance to change',
                'Budget constraints',
                'Infrastructure limitations',
              ]}
            />
          </Box>
        </Box>
      </SlideContainer>

      {/* Slide 11: Platform */}
      <SlideContainer active={currentSlide === 10} bg={COLORS.gradient1}>
        <Box sx={{ textAlign: 'center', maxWidth: 900 }}>
          <Typography variant="h2" fontWeight={800} color="white" sx={{ mb: 5 }}>
            Available On All Platforms
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 6, flexWrap: 'wrap' }}>
            <Zoom in={currentSlide === 10} style={{ transitionDelay: '100ms' }}>
              <Box
                sx={{
                  p: 4,
                  borderRadius: 4,
                  background: 'rgba(255,255,255,0.1)',
                  textAlign: 'center',
                  minWidth: 280,
                }}
              >
                <Computer sx={{ fontSize: 80, color: 'white', mb: 2 }} />
                <Typography variant="h4" color="white" fontWeight={700} gutterBottom>
                  Web App
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.7)' }}>
                  Full dashboard, reporting, admin controls
                </Typography>
              </Box>
            </Zoom>
            <Zoom in={currentSlide === 10} style={{ transitionDelay: '300ms' }}>
              <Box
                sx={{
                  p: 4,
                  borderRadius: 4,
                  background: 'rgba(255,255,255,0.1)',
                  textAlign: 'center',
                  minWidth: 280,
                }}
              >
                <PhoneAndroid sx={{ fontSize: 80, color: 'white', mb: 2 }} />
                <Typography variant="h4" color="white" fontWeight={700} gutterBottom>
                  Mobile App
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.7)' }}>
                  QR scanning, work orders, notifications
                </Typography>
              </Box>
            </Zoom>
          </Box>
        </Box>
      </SlideContainer>

      {/* Slide 12: Call to Action */}
      <SlideContainer active={currentSlide === 11} bg={COLORS.gradient3}>
        <Box sx={{ textAlign: 'center', maxWidth: 800 }}>
          <Zoom in={currentSlide === 11}>
            <Box>
              <Typography
                variant="h2"
                fontWeight={900}
                color="white"
                sx={{ mb: 3, fontSize: { xs: '2.5rem', md: '3.5rem' } }}
              >
                Ready to Transform UCI's Maintenance Operations?
              </Typography>
              <Typography
                variant="h5"
                sx={{ color: 'rgba(255,255,255,0.9)', mb: 5, fontWeight: 400 }}
              >
                Join the digital transformation journey for better patient care
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap' }}>
                <Chip
                  icon={<PlayArrow />}
                  label="Schedule Demo"
                  sx={{
                    backgroundColor: 'white',
                    color: COLORS.accent,
                    fontWeight: 700,
                    fontSize: '1.1rem',
                    py: 3,
                    px: 2,
                    '&:hover': { backgroundColor: '#f5f5f5' },
                  }}
                  clickable
                />
                <Chip
                  label="Contact Us"
                  sx={{
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    fontWeight: 700,
                    fontSize: '1.1rem',
                    py: 3,
                    px: 2,
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' },
                  }}
                  clickable
                />
              </Box>
              <Typography
                sx={{ color: 'rgba(255,255,255,0.5)', mt: 6, fontSize: '0.9rem' }}
              >
                © {new Date().getFullYear()} Uganda Cancer Institute
              </Typography>
            </Box>
          </Zoom>
        </Box>
      </SlideContainer>
    </Box>
  )
}

// Generate downloadable HTML
const generateDownloadHTML = () => `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>DuoNep Presentation</title>
  <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap" rel="stylesheet">
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Inter', sans-serif; background: #0a1628; color: white; overflow-x: hidden; }
    .slide { min-height: 100vh; display: flex; align-items: center; justify-content: center; padding: 60px 40px; position: relative; }
    .slide-content { max-width: 1000px; width: 100%; text-align: center; }
    .gradient-1 { background: linear-gradient(135deg, #1B5E7A 0%, #0d3d4d 100%); }
    .gradient-2 { background: linear-gradient(135deg, #2E7D32 0%, #1B5E20 100%); }
    .gradient-3 { background: linear-gradient(135deg, #FF6B35 0%, #E64A19 100%); }
    .gradient-4 { background: linear-gradient(135deg, #9C27B0 0%, #7B1FA2 100%); }
    .gradient-5 { background: linear-gradient(135deg, #0a1628 0%, #1a2744 100%); }
    h1 { font-size: 5rem; font-weight: 900; margin-bottom: 1rem; letter-spacing: -0.03em; }
    h2 { font-size: 3rem; font-weight: 800; margin-bottom: 1.5rem; }
    h3 { font-size: 1.5rem; font-weight: 700; margin-bottom: 0.5rem; }
    p { font-size: 1.25rem; opacity: 0.85; line-height: 1.7; }
    .subtitle { font-size: 1.75rem; font-weight: 300; opacity: 0.9; margin-bottom: 2rem; }
    .badge { display: inline-block; padding: 8px 20px; background: rgba(255,255,255,0.15); border-radius: 50px; font-weight: 600; margin: 4px; }
    .grid { display: grid; gap: 24px; margin-top: 40px; }
    .grid-2 { grid-template-columns: repeat(2, 1fr); }
    .grid-3 { grid-template-columns: repeat(3, 1fr); }
    .grid-4 { grid-template-columns: repeat(4, 1fr); }
    @media (max-width: 768px) { .grid-2, .grid-3, .grid-4 { grid-template-columns: 1fr; } h1 { font-size: 3rem; } h2 { font-size: 2rem; } }
    .card { background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1); border-radius: 16px; padding: 24px; }
    .icon-box { width: 80px; height: 80px; border-radius: 20px; display: flex; align-items: center; justify-content: center; margin: 0 auto 20px; font-size: 2rem; }
    .stat-number { font-size: 4rem; font-weight: 900; }
    .feature-list { text-align: left; margin-top: 24px; }
    .feature-list li { padding: 12px 0; padding-left: 32px; position: relative; font-size: 1.1rem; }
    .feature-list li:before { content: "✓"; position: absolute; left: 0; color: #4CAF50; font-weight: bold; }
    .swot-card { padding: 20px; border-radius: 12px; text-align: left; }
    .swot-strengths { background: rgba(76, 175, 80, 0.15); border: 2px solid rgba(76, 175, 80, 0.4); }
    .swot-weaknesses { background: rgba(255, 152, 0, 0.15); border: 2px solid rgba(255, 152, 0, 0.4); }
    .swot-opportunities { background: rgba(33, 150, 243, 0.15); border: 2px solid rgba(33, 150, 243, 0.4); }
    .swot-threats { background: rgba(244, 67, 54, 0.15); border: 2px solid rgba(244, 67, 54, 0.4); }
    .cta-btn { display: inline-block; padding: 16px 40px; border-radius: 12px; font-weight: 700; font-size: 1.1rem; margin: 8px; text-decoration: none; }
    .cta-primary { background: white; color: #FF6B35; }
    .cta-secondary { background: rgba(255,255,255,0.2); color: white; }
  </style>
</head>
<body>
  <div class="slide gradient-1">
    <div class="slide-content">
      <span class="badge">Uganda Cancer Institute</span>
      <h1 style="margin-top: 24px;">DuoNep</h1>
      <p class="subtitle">Computerized Maintenance Management System</p>
      <p style="max-width: 600px; margin: 0 auto;">A comprehensive digital solution for managing medical equipment maintenance, inventory, and facility operations</p>
    </div>
  </div>

  <div class="slide gradient-5">
    <div class="slide-content">
      <h2>The Challenge</h2>
      <p style="opacity: 0.7; margin-bottom: 40px;">Healthcare facilities face critical challenges in equipment management</p>
      <div class="grid grid-2">
        <div class="card" style="border-left: 4px solid #F44336;"><h3>Equipment Downtime</h3><p>Unexpected failures cause treatment delays</p></div>
        <div class="card" style="border-left: 4px solid #FF9800;"><h3>Manual Tracking</h3><p>Paper records get lost, schedules missed</p></div>
        <div class="card" style="border-left: 4px solid #9C27B0;"><h3>Inventory Chaos</h3><p>No visibility into spare parts stock</p></div>
        <div class="card" style="border-left: 4px solid #2196F3;"><h3>Compliance Risk</h3><p>Difficulty maintaining audit trails</p></div>
      </div>
    </div>
  </div>

  <div class="slide gradient-2">
    <div class="slide-content">
      <h2>Our Solution</h2>
      <p class="subtitle">DuoNep - One Platform for All Maintenance Operations</p>
      <p style="max-width: 700px; margin: 0 auto;">A modern, web and mobile-based system that digitizes and streamlines all maintenance workflows</p>
    </div>
  </div>

  <div class="slide gradient-5">
    <div class="slide-content">
      <h2>Key Features</h2>
      <div class="grid grid-3">
        <div class="card"><div class="icon-box" style="background: rgba(27, 94, 122, 0.2);">🏥</div><h3>Asset Management</h3><p>Complete lifecycle tracking with QR codes</p></div>
        <div class="card"><div class="icon-box" style="background: rgba(46, 125, 50, 0.2);">🔧</div><h3>Work Orders</h3><p>Streamlined maintenance workflows</p></div>
        <div class="card"><div class="icon-box" style="background: rgba(255, 107, 53, 0.2);">📅</div><h3>Preventive Maintenance</h3><p>Automated scheduling & tracking</p></div>
        <div class="card"><div class="icon-box" style="background: rgba(156, 39, 176, 0.2);">📦</div><h3>Inventory Control</h3><p>Real-time stock management</p></div>
        <div class="card"><div class="icon-box" style="background: rgba(0, 150, 136, 0.2);">⚡</div><h3>Calibration</h3><p>Schedule & certificate tracking</p></div>
        <div class="card"><div class="icon-box" style="background: rgba(244, 67, 54, 0.2);">☢️</div><h3>Oncology</h3><p>Specialized QA/QC & source tracking</p></div>
      </div>
    </div>
  </div>

  <div class="slide gradient-5">
    <div class="slide-content">
      <h2>Expected Impact</h2>
      <p style="opacity: 0.7; margin-bottom: 40px;">Measurable improvements in maintenance operations</p>
      <div class="grid grid-4">
        <div><div class="stat-number" style="color: #4CAF50;">40%</div><p>Reduced Downtime</p></div>
        <div><div class="stat-number" style="color: #2196F3;">60%</div><p>Faster Response</p></div>
        <div><div class="stat-number" style="color: #FF9800;">100%</div><p>Audit Compliance</p></div>
        <div><div class="stat-number" style="color: #9C27B0;">25%</div><p>Cost Savings</p></div>
      </div>
    </div>
  </div>

  <div class="slide gradient-5">
    <div class="slide-content">
      <h2>SWOT Analysis</h2>
      <div class="grid grid-2" style="margin-top: 30px;">
        <div class="swot-card swot-strengths"><h3 style="color: #4CAF50;">💪 Strengths</h3><ul class="feature-list"><li>Custom-built for UCI needs</li><li>Web + Mobile accessibility</li><li>Role-based access control</li><li>Real-time notifications</li></ul></div>
        <div class="swot-card swot-weaknesses"><h3 style="color: #FF9800;">⚠️ Weaknesses</h3><ul class="feature-list"><li>Initial data migration effort</li><li>Staff training required</li><li>Internet dependency</li></ul></div>
        <div class="swot-card swot-opportunities"><h3 style="color: #2196F3;">🚀 Opportunities</h3><ul class="feature-list"><li>Integration with HIS systems</li><li>Expansion to other facilities</li><li>Predictive maintenance (AI)</li><li>IoT sensor integration</li></ul></div>
        <div class="swot-card swot-threats"><h3 style="color: #F44336;">🛡️ Threats</h3><ul class="feature-list"><li>Resistance to change</li><li>Budget constraints</li><li>Infrastructure limitations</li></ul></div>
      </div>
    </div>
  </div>

  <div class="slide gradient-3">
    <div class="slide-content">
      <h2>Ready to Transform UCI's Maintenance Operations?</h2>
      <p class="subtitle">Join the digital transformation journey for better patient care</p>
      <div style="margin-top: 40px;">
        <a href="#" class="cta-btn cta-primary">Schedule Demo</a>
        <a href="#" class="cta-btn cta-secondary">Contact Us</a>
      </div>
      <p style="margin-top: 60px; opacity: 0.5; font-size: 0.9rem;">© ${new Date().getFullYear()} Uganda Cancer Institute</p>
    </div>
  </div>
</body>
</html>
`

export default PresentationPage
